import { useCallback } from 'react';
import { useCookie } from 'react-use';

import { startOfDay } from 'date-fns';
import isNil from 'lodash/isNil';

import { dateHelpers } from '@nocowanie/core';
import { defaultGuestsPickerConfig } from '@nocowanie/common-ui';

import { CookieName } from '../enums';
import { parseCookies } from '../helpers';
import { SearchEngineModel } from '../models';

const decodeCookie = (cookieValue: string | null) => {
    return cookieValue ? JSON.parse(atob(cookieValue)) : {};
};

export const useSearchCookie = (domain: string) => {
    const [value, updateCookie] = useCookie(CookieName.SearchCriteria);
    const currentParams = decodeCookie(value);

    const setCookie = useCallback(
        (params: Partial<SearchEngineModel>) => {
            const adultsCount = params.guests?.adultsCount;
            const childrenCount = params.guests?.childrenCount || 0;

            const getInstantReservation = () => {
                if (!isNil(params.instantReservation)) {
                    return params.instantReservation ? '1' : '0';
                }

                return currentParams?.instantReservation;
            };

            const data = {
                miejsca_dorosli: adultsCount,
                miejsca_dzieci: childrenCount,
                miejsca: adultsCount ? adultsCount + childrenCount : undefined,
                wiek_dzieci: params.guests?.childrenAges?.join(','),
                rezerwacja_online: getInstantReservation(),
                flex_dates: params.flexDates,
                data:
                    params.dates?.checkInDate &&
                    params.dates.checkOutDate &&
                    dateHelpers.isValidRange(params.dates.checkInDate, params.dates.checkOutDate)
                        ? {
                              od: dateHelpers.format(params.dates.checkInDate, 'yyyy-MM-dd'),
                              do: dateHelpers.format(params.dates.checkOutDate, 'yyyy-MM-dd'),
                          }
                        : undefined,
                timestamp: Date.now(),
            };

            updateCookie(btoa(JSON.stringify(data)), {
                domain,
                expires: 7,
                secure: true,
            });
        },
        [updateCookie, domain, currentParams?.instantReservation],
    );

    const getSearchCriteriaFromCookie = useCallback((): Partial<SearchEngineModel> | null => {
        const cookies = parseCookies(document.cookie);
        const decoded = decodeCookie(cookies[CookieName.SearchCriteria]);

        if (!decoded || !decoded.data) {
            return null;
        }

        return {
            dates: {
                checkInDate: decoded.data.od ? startOfDay(new Date(decoded.data.od)) : null,
                checkOutDate: decoded.data.do ? startOfDay(new Date(decoded.data.do)) : null,
            },
            guests: {
                adultsCount:
                    decoded.miejsca_dorosli ?? defaultGuestsPickerConfig.adultsConfig.default,
                childrenCount:
                    decoded.miejsca_dzieci ?? defaultGuestsPickerConfig.childrenConfig.default,
                childrenAges:
                    decoded.wiek_dzieci && decoded.wiek_dzieci.trim().length > 0
                        ? decoded.wiek_dzieci.split(',').map(Number)
                        : [],
            },
            flexDates: decoded.flex_dates,
        };
    }, []);

    return {
        setCookie,
        getSearchCriteriaFromCookie,
    };
};
